import React, { useRef, useState, useEffect } from "react";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import useComponentVisible from "../../hooks/useComponentVisible";
import useScrollLock from "../../hooks/useScrollLock";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { Eye, EyeOff } from "lucide-react";

import "swiper/css/pagination";

import {
  loginAction,
  changeSignupModalDisplay,
  requestOTPAction,
  verifyOTPAction,
} from "../../actions/authActions";
import { clearServerErrors } from "../../actions/errors";

const Login = ({
  loginAction,
  requestOTPAction,
  verifyOTPAction,
  serverErrors,
  clearServerErrors,
  loginModalDisplay,
  setLoginModalDisplay,
  changeSignupModalDisplay,
}) => {
  const [langSwitcherRef, isLangSwicherVisalbe, setIsLangSwicherVisable] =
    useComponentVisible(false);
  const { t, i18n } = useTranslation();

  // OTP login state
  const [loginMethod, setLoginMethod] = useState("email"); // "email" or "phone"
  const [otpStep, setOtpStep] = useState("phone"); // "phone" or "code"
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  useScrollLock(loginModalDisplay === "block");

  const LoginSchema = yup.object().shape({
    email: yup.string().email().required(),
    password: yup.string().required(),
  });
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({ mode: "onSubmit", resolver: yupResolver(LoginSchema) });

  useEffect(() => {
    clearServerErrors();
    setOtpError("");
    setCountdown(0);
  }, [loginMethod]);

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      setOtpError(t("phone number is required"));
      return;
    }
    setOtpLoading(true);
    setOtpError("");
    const result = await requestOTPAction(phoneNumber);
    setOtpLoading(false);
    if (result.success) {
      setOtpStep("code");
      setCountdown(180); // 3 minutes countdown
    } else {
      // Handle rate limiting - extract seconds_remaining if present
      if (result.seconds_remaining) {
        setCountdown(result.seconds_remaining);
      }
      setOtpError(result.error);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setOtpLoading(true);
    setOtpError("");
    setOtpCode("");
    const result = await requestOTPAction(phoneNumber);
    setOtpLoading(false);
    if (result.success) {
      setCountdown(180);
    } else {
      if (result.seconds_remaining) {
        setCountdown(result.seconds_remaining);
      }
      setOtpError(result.error);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) {
      setOtpError(t("otp code is required"));
      return;
    }
    setOtpLoading(true);
    setOtpError("");
    const result = await verifyOTPAction(phoneNumber, otpCode);
    setOtpLoading(false);
    if (result.success) {
      setLoginModalDisplay("none");
    } else {
      setOtpError(result.error);
    }
  };

  return (
    <div
      style={{ display: loginModalDisplay }}
      id="myModalL"
      className="modal-background"
    >
      <div className="login-container animate__slideInDown">
        <span
          className="close_login"
          onClick={() => setLoginModalDisplay("none")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <g>
              <g>
                <path
                  stroke="#000"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18 18l-6-6m0 0L6 6m6 6l6-6m-6 6l-6 6"
                ></path>
              </g>
            </g>
          </svg>

          {/* {t("close modal")} */}
        </span>

        <div className="login-form-container">
          <h4 className="login-title">{t("login")}</h4>

          {/* Login Method Tabs */}
          <div className="login-method-tabs">
            <button
              type="button"
              className={`login-tab ${loginMethod === "email" ? "active" : ""}`}
              onClick={() => {
                setLoginMethod("email");
                setOtpStep("phone");
                setOtpError("");
              }}
            >
              {t("email")}
            </button>
            <button
              type="button"
              className={`login-tab ${loginMethod === "phone" ? "active" : ""}`}
              onClick={() => {
                setLoginMethod("phone");
                clearServerErrors();
              }}
            >
              {t("phone")}
            </button>
          </div>

          {/* Email/Password Login Form */}
          {loginMethod === "email" && (
            <form
              onSubmit={handleSubmit(loginAction)}
              noValidate
              autoComplete="email"
            >
              <label>
                <input
                  type="email"
                  placeholder={t("Enter your email")}
                  {...register("email")}
                />
              </label>
              {errors.email && (
                <a className="error-text">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1024 1024"
                  >
                    <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                  </svg>
                  {t(errors.email?.message.key)}
                </a>
              )}

              <label>
                <input
                  className="input-user-password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("Enter password")}
                  {...register("password")}
                />

                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </label>

              {errors.password && (
                <a className="error-text">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1024 1024"
                  >
                    <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                  </svg>
                  {t(errors.password?.message.key)}
                </a>
              )}
              {serverErrors.error && (
                <a className="server-error-text">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1024 1024"
                  >
                    <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                  </svg>
                  {t(serverErrors.error)}
                </a>
              )}
              <input type="submit" value={t("login")} />
              <div className="forgetpass-container">
                {t("forgot_password")} <a>-</a>{" "}
                <a href="/request/reset/password">{t("recover password")}</a>
              </div>
              <div className="login-signup-switcher">
                <p
                  className="create-account"
                  onClick={() => {
                    setLoginModalDisplay("none");
                    changeSignupModalDisplay("block");
                  }}
                >
                  {t("don't have an account?")} <a>-</a>{" "}
                  <span>{t("sign_up")}</span>
                </p>
              </div>
            </form>
          )}

          {/* Phone/OTP Login Form */}
          {loginMethod === "phone" && (
            <form
              onSubmit={
                otpStep === "phone" ? handleRequestOTP : handleVerifyOTP
              }
            >
              {otpStep === "phone" ? (
                <>
                  <label>
                    <input
                      type="tel"
                      placeholder="5XX XXX XXX"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </label>
                  <p className="otp-info-text">
                    {t("otp will be sent to your phone")}
                  </p>
                </>
              ) : (
                <>
                  <p className="otp-sent-text">
                    {t("otp sent to")} {phoneNumber}
                  </p>
                  <label>
                    <input
                      type="text"
                      placeholder={t("Enter OTP code")}
                      value={otpCode}
                      onChange={(e) =>
                        setOtpCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      maxLength={6}
                      style={{ textAlign: "center", letterSpacing: "0.3rem" }}
                    />
                  </label>
                </>
              )}
              {otpError && (
                <a className="error-text">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1024 1024"
                  >
                    <path d="M512 64a448 448 0 110 896 448 448 0 010-896zm0 832a384 384 0 000-768 384 384 0 000 768zm48-176a48 48 0 11-96 0 48 48 0 0196 0zm-48-464a32 32 0 0132 32v288a32 32 0 01-64 0V288a32 32 0 0132-32z"></path>
                  </svg>
                  {otpError}
                </a>
              )}
              <input
                type="submit"
                value={
                  otpLoading
                    ? t("loading")
                    : otpStep === "phone"
                      ? t("send otp")
                      : t("verify and login")
                }
                disabled={otpLoading}
              />
              {otpStep === "code" && (
                <>
                  <button
                    type="button"
                    className="resend-otp-btn"
                    onClick={handleResendOTP}
                    disabled={countdown > 0 || otpLoading}
                    style={{
                      marginTop: "10px",
                      background: countdown > 0 ? "#ccc" : "#f5f5f5",
                      color: countdown > 0 ? "#999" : "#333",
                      border: "1px solid #ddd",
                      padding: "10px 20px",
                      borderRadius: "5px",
                      cursor: countdown > 0 ? "not-allowed" : "pointer",
                      width: "100%",
                      fontSize: "14px",
                    }}
                  >
                    {countdown > 0
                      ? `${t("otp_resend")} (${Math.floor(countdown / 60)}:${(
                          countdown % 60
                        )
                          .toString()
                          .padStart(2, "0")})`
                      : t("otp_resend")}
                  </button>
                  <button
                    type="button"
                    className="change-phone-btn"
                    onClick={() => {
                      setOtpStep("phone");
                      setOtpCode("");
                      setOtpError("");
                      setCountdown(0);
                    }}
                    style={{
                      marginTop: "10px",
                      background: "transparent",
                      color: "#666",
                      border: "none",
                      padding: "10px",
                      cursor: "pointer",
                      fontSize: "14px",
                      textDecoration: "underline",
                    }}
                  >
                    {t("change phone number")}
                  </button>
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    serverErrors: state.errors.errors,
  };
};

export default connect(mapStateToProps, {
  changeSignupModalDisplay,
  loginAction,
  requestOTPAction,
  verifyOTPAction,
  clearServerErrors,
})(Login);
